/**
 * Friday Paddle Quiz
 *
 * - 5 questions (level, priority, feel, budget, gen)
 * - Conditional shape question for advanced/pro users
 * - Email gate before results (skippable)
 * - Full ranked result list: best match + all runners-up with "why"
 * - Klaviyo: subscribe + fire custom event with full answers/results
 */

// Guard against double-loading: on the standalone quiz page this snippet renders twice
// (page section + cart drawer), so the asset is requested twice. Without this guard the
// second execution throws "Identifier 'QUESTIONS' has already been declared".
if (!window.__paddleQuizLoaded) {
window.__paddleQuizLoaded = true;

// Email capture step before results. ON — Klaviyo flow is live and the event fires on
// email submit AND on skip (applies to both the page and drawer quiz).
const SHOW_EMAIL_STEP = true;

// PostHog quiz funnel events (FRI-362). No-ops when PostHog is absent.
const phQuiz = (name, props) => {
  if (window.posthog && typeof window.posthog.capture === 'function') {
    window.posthog.capture(name, Object.assign({ quiz: 'paddle-quiz' }, props));
  }
};

const QUESTIONS = [
  {
    id: 'level',
    question: "What's your skill level?",
    subtitle: "Be honest. We'll find the right fit either way.",
    options: [
      { value: 'beginner',     label: 'Just starting out',   desc: 'Learning the basics, want something forgiving', icon: '🌱' },
      { value: 'intermediate', label: 'Getting competitive', desc: "I can rally and I'm working on my game",        icon: '🔥' },
      { value: 'advanced',     label: 'Tournament ready',    desc: 'I compete and I want every edge',               icon: '🏆' },
      { value: 'pro',          label: 'I live on the court', desc: '4.5+ DUPR, this is my sport',                   icon: '⚡' },
    ],
  },
  {
    id: 'priority',
    question: 'What matters most to you?',
    subtitle: "Pick the one thing you'd optimize for.",
    options: [
      { value: 'control',  label: 'Control & touch',   desc: 'Precise dinks, drops, and placement', icon: '🎯' },
      { value: 'power',    label: 'Raw power',         desc: 'Drives that clear the baseline',      icon: '💥' },
      { value: 'allcourt', label: 'All-court balance', desc: 'I want to do everything well',        icon: '⚖️' },
      { value: 'spin',     label: 'Spin & shape',      desc: 'I want to manipulate every shot',     icon: '🌀' },
    ],
  },
  {
    id: 'feel',
    question: 'How do you want it to feel?',
    subtitle: "This one's personal. Trust your gut.",
    options: [
      { value: 'soft',         label: 'Soft & plush',       desc: 'Cradle the ball, quiet and controlled',       icon: '🧸' },
      { value: 'poppy',        label: 'Poppy & lively',     desc: 'Explosive off the face, you feel the launch', icon: '🚀' },
      { value: 'connected',    label: 'Connected & stable', desc: 'Solid contact, predictable response',         icon: '🔗' },
      { value: 'nopreference', label: 'No preference',      desc: "I'll try anything. Surprise me.",             icon: '🎲' },
    ],
  },
  {
    id: 'budget',
    question: "What's your budget?",
    subtitle: 'Every Friday paddle punches above its price tag.',
    options: [
      { value: 'any',      label: 'Whatever it takes', desc: 'Just find me the right paddle',         icon: '💎' },
      { value: '130plus',  label: '$130+',             desc: 'I want the best Friday has to offer',   icon: '🧡' },
      { value: '75to130',  label: '$75 to $130',       desc: 'The performance sweet spot',            icon: '💛' },
      { value: 'under75',  label: 'Under $75',         desc: 'Best value in the game',                icon: '💚' },
    ],
  },
  {
    id: 'gen',
    question: 'Gen 3 or Gen 4?',
    subtitle: 'Gen 3 uses a honeycomb core for a more connected feel. Gen 4 uses a foam core for more power and durability.',
    options: [
      { value: 'gen3',   label: 'Gen 3',        desc: 'I like feeling the ball, shaping shots',      icon: '🎨' },
      { value: 'gen4',   label: 'Gen 4',        desc: 'I want foam for power, stability, durability', icon: '⚡' },
      { value: 'either', label: 'Either works', desc: 'Match me based on everything else',           icon: '🤷' },
    ],
  },
];

const SHAPE_QUESTION = {
  id: 'shape',
  question: 'Which paddle shape do you prefer?',
  subtitle: "You're skilled enough that shape matters. Pick your weapon.",
  options: [
    { value: 'elongated', label: 'Elongated', desc: 'More reach, whip, and spin on drives',        icon: '📏' },
    { value: 'widebody',  label: 'Widebody',  desc: 'Biggest sweet spot, most forgiving',          icon: '🛡️' },
    { value: 'hybrid',    label: 'Hybrid',    desc: 'Split the difference, versatile all-court',   icon: '⚖️' },
    { value: 'notsure',   label: 'Not sure',  desc: 'Match me based on my other answers',          icon: '🤷' },
  ],
};

function scorePaddle(paddle, answers) {
  let score = 0;
  const { level, priority, feel, budget, gen, shape } = answers;
  const key = paddle.key;

  // LEVEL: dominant axis. Fever 102 weights are always > Fever 101 for intermediate+ (Andre: push 102 over 101).
  // Intermediate+ defaults to Aura / Aura Pro — Fever should only win when the user picks a budget tier
  // that knocks Aura out, or explicitly asks for Gen 3.
  // Beginner exception: Fever 101 inherits the "step-up from Original" value slot the (discontinued)
  // Challenger used to hold, so beginner fever101 (20) intentionally sits above fever102 (14).
  const levelMap = {
    beginner:     { original: 25, fever101: 20, fever102: 14, aura: 5,  auraPro: 3  },
    intermediate: { original: 5,  fever101: 12, fever102: 15, aura: 18, auraPro: 26 },
    advanced:     { original: 0,  fever101: 8,  fever102: 17, aura: 18, auraPro: 28 },
    pro:          { original: 0,  fever101: 5,  fever102: 14, aura: 16, auraPro: 30 },
  };
  score += (levelMap[level]?.[key] || 0);

  // AURA PRO is the default hero for intermediate+ (Andre: push Aura Pro over Aura in ~99% of
  // cases). Aura only reclaims the top when the player explicitly prioritizes feel/control/touch
  // (priority "control" or feel "soft"). Budget still applies separately — a sub-Aura-Pro budget
  // can still knock it out of range.
  const advancedish = level === 'intermediate' || level === 'advanced' || level === 'pro';
  const feelFirst = priority === 'control' || feel === 'soft';
  if (advancedish && key === 'auraPro' && feelFirst === false) score += 12;
  if (advancedish && key === 'aura'    && feelFirst === true)  score += 22;

  // PRIORITY
  if (priority === 'control')  score += paddle.control * 2;
  if (priority === 'power')    score += paddle.power * 2;
  if (priority === 'allcourt') score += (paddle.power + paddle.control + paddle.spin) * 0.7;
  if (priority === 'spin')     score += paddle.spin * 2;

  // FEEL
  if (feel === 'soft') {
    if (key === 'aura')          score += 15;
    else if (key === 'fever102') score += 8;
    else if (key === 'original') score += 6;
  }
  if (feel === 'poppy') {
    if (key === 'auraPro')       score += 15;
    else if (key === 'fever102') score += 13; // bumped above 101
    else if (key === 'fever101') score += 10;
  }
  if (feel === 'connected') {
    if (key === 'fever102') score += 12;
    if (key === 'auraPro')  score += 10;
    if (key === 'aura')     score += 8;
  }

  // BUDGET: uses live price from Liquid (priceDollars)
  const price = paddle.priceDollars || 0;
  if (budget === 'under75') {
    if (price <= 75) score += 15;
    else score -= 20;
  }
  if (budget === '75to130') {
    // Andre: within this tier, push Aura > Fever 102 > Fever 101
    if (price >= 75 && price <= 130) {
      score += 15;
      if (key === 'aura')         score += 10;
      else if (key === 'fever102') score += 7;
      else if (key === 'fever101') score += 4;
    } else if (price < 75) {
      score += 3;
    } else {
      score -= 10;
    }
  }
  if (budget === '130plus') {
    if (price >= 130) score += 15;
    else score += 2;
  }
  if (budget === 'any') {
    // "Whatever it takes" — push toward premium tier (Aura, Aura Pro)
    if (price >= 125) score += 12;
  }

  // GEN: strong swing so explicit gen3 / gen4 selection reliably flips results
  if (gen === 'gen3') {
    if (paddle.gen === 'Gen 3') score += 18;
    else if (paddle.gen === 'Gen 4') score -= 10;
  }
  if (gen === 'gen4') {
    if (paddle.gen === 'Gen 4') score += 18;
    else if (paddle.gen === 'Gen 3') score -= 10;
  }

  // SHAPE (advanced/pro only)
  if (shape && shape !== 'notsure') {
    const shapeMap = { elongated: 'Elongated', widebody: 'Widebody', hybrid: 'Hybrid' };
    const wanted = shapeMap[shape];
    if (wanted && paddle.shapes.includes(wanted)) score += 8;
    else if (wanted) score -= 3;
  }

  // Universal tiebreaker: Fever 102 always beats Fever 101
  if (key === 'fever102') score += 2;

  return score;
}

const STORAGE_KEY = 'friday_paddle_quiz_state_v1';

function loadStoredState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== 1) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

function saveStoredState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: 1,
      answers: state.answers,
      scored: state.scored,
    }));
  } catch (e) {
    /* quota exceeded or disabled — fail silently */
  }
}

function clearStoredState() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
}

function buildQuestionList(level) {
  const list = [...QUESTIONS];
  if (level === 'advanced' || level === 'pro') {
    const insertAt = list.findIndex(q => q.id === 'gen');
    list.splice(insertAt >= 0 ? insertAt : list.length, 0, SHAPE_QUESTION);
  }
  return list;
}

class PaddleQuiz extends HTMLElement {
  constructor() {
    super();
    this.state = {
      screen: 'intro',
      step: 0,
      answers: {},
      questions: QUESTIONS,
      scored: [],
    };
  }

  connectedCallback() {
    try {
      const dataEl = this.querySelector('.paddle-quiz__data');
      const data = JSON.parse(dataEl.textContent);
      this.paddles = data.paddles;
    } catch (err) {
      console.error('[paddle-quiz] Failed to parse paddle data', err);
      this.paddles = {};
    }

    // Collect the pre-rendered PDP price HTML for each paddle
    this.priceTemplates = {};
    this.querySelectorAll('[data-paddle-price]').forEach(tpl => {
      this.priceTemplates[tpl.dataset.paddlePrice] = tpl.innerHTML.trim();
    });

    this.klaviyoListId = this.dataset.klaviyoListId || '';
    this.klaviyoPublicKey = this.dataset.klaviyoPublicKey || '';
    this.klaviyoEventName = this.dataset.klaviyoEventName || 'Submitted Paddle Quiz';

    this.$screens = {
      intro:    this.querySelector('[data-screen="intro"]'),
      question: this.querySelector('[data-screen="question"]'),
      email:    this.querySelector('[data-screen="email"]'),
      results:  this.querySelector('[data-screen="results"]'),
    };
    this.$questionBody = this.querySelector('[data-question-body]');
    this.$resultsBody  = this.querySelector('[data-results-body]');
    this.$progressBar  = this.querySelector('[data-progress-bar]');
    this.$progressCount = this.querySelector('[data-progress-count]');
    this.$backLink     = this.querySelector('[data-back-link]');
    this.$emailForm    = this.querySelector('[data-email-form]');
    this.$emailError   = this.querySelector('[data-email-error]');

    this.addEventListener('click', this.handleClick);
    this.$emailForm?.addEventListener('submit', this.handleEmailSubmit);

    // Restore prior session if present
    this.restoreFromStorage();
  }

  restoreFromStorage() {
    const saved = loadStoredState();
    if (!saved?.answers) return;

    const hasAnswers = Object.keys(saved.answers).length > 0;
    if (!hasAnswers) return;

    this.state.answers = saved.answers;
    this.state.questions = buildQuestionList(saved.answers.level);

    // Completed quiz: rehydrate scored list and jump to results.
    // Re-score from live paddle data in case editorial copy changed; fall back
    // to stored scored if re-scoring fails (e.g. stored answers reference a
    // budget/gen option that no longer exists).
    if (saved.scored && saved.scored.length) {
      try {
        this.state.scored = Object.values(this.paddles).map(p => ({
          paddle: p,
          score: scorePaddle(p, saved.answers),
        })).sort((a, b) => b.score - a.score);
      } catch (err) {
        this.state.scored = saved.scored;
      }
      this.renderResults();
      return;
    }

    // Partial quiz: resume at next unanswered question.
    const nextStep = this.state.questions.findIndex(q => !(q.id in saved.answers));
    this.state.step = nextStep === -1 ? this.state.questions.length - 1 : nextStep;
    this.showScreen('question');
    this.renderQuestion();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick = (e) => {
    const actionBtn = e.target.closest('[data-action]');
    if (!actionBtn || !this.contains(actionBtn)) return;
    const action = actionBtn.dataset.action;

    if (action === 'start')      return this.start();
    if (action === 'restart')    return this.restart();
    if (action === 'back')       return this.back();
    if (action === 'answer')     return this.answer(actionBtn.dataset.questionId, actionBtn.dataset.value);
    if (action === 'skip-email') return this.skipEmail();
  };

  showScreen(name) {
    Object.entries(this.$screens).forEach(([key, el]) => {
      if (!el) return;
      el.hidden = key !== name;
    });
    this.state.screen = name;
  }

  start() {
    this.state.step = 0;
    this.state.answers = {};
    this.state.questions = [...QUESTIONS];
    phQuiz('quiz_started', { context: this.dataset.context });
    this.showScreen('question');
    this.renderQuestion();
  }

  restart() {
    this.state.scored = [];
    clearStoredState();
    this.start();
  }

  renderQuestion() {
    const q = this.state.questions[this.state.step];
    if (!q) return this.finishQuestions();

    const total = this.state.questions.length;
    const current = this.state.step + 1;
    this.$progressCount.textContent = `${current} / ${total}`;
    this.$progressBar.style.width = `${(current / total) * 100}%`;
    if (this.$backLink) this.$backLink.hidden = this.state.step <= 0;

    const selected = this.state.answers[q.id];

    this.$questionBody.innerHTML = `
      <h3 class="paddle-quiz__q-title heading">${q.question}</h3>
      <p class="paddle-quiz__q-sub">${q.subtitle}</p>
      <div class="paddle-quiz__options">
        ${q.options.map(opt => `
          <button
            type="button"
            class="paddle-quiz__option ${selected === opt.value ? 'is-selected' : ''}"
            data-action="answer"
            data-question-id="${q.id}"
            data-value="${opt.value}"
          >
            <span class="paddle-quiz__option-icon" aria-hidden="true">${opt.icon}</span>
            <span class="paddle-quiz__option-text">
              <span class="paddle-quiz__option-label">${opt.label}</span>
              <span class="paddle-quiz__option-desc">${opt.desc}</span>
            </span>
          </button>
        `).join('')}
      </div>
    `;

    this.$questionBody.classList.remove('is-entering');
    void this.$questionBody.offsetWidth;
    this.$questionBody.classList.add('is-entering');
  }

  answer(questionId, value) {
    this.state.answers[questionId] = value;

    if (questionId === 'level') {
      const isAdvancedTier = (value === 'advanced' || value === 'pro');
      const hasShape = this.state.questions.some(q => q.id === 'shape');
      if (isAdvancedTier && !hasShape) {
        const insertAt = this.state.questions.findIndex(q => q.id === 'gen');
        this.state.questions.splice(insertAt >= 0 ? insertAt : this.state.questions.length, 0, SHAPE_QUESTION);
      } else if (!isAdvancedTier && hasShape) {
        // Going back and downgrading level — drop the shape question and any answer for it
        const removeAt = this.state.questions.findIndex(q => q.id === 'shape');
        if (removeAt >= 0) this.state.questions.splice(removeAt, 1);
        delete this.state.answers.shape;
      }
    }

    // Persist partial progress so a mid-quiz page nav resumes where they left off
    saveStoredState(this.state);

    setTimeout(() => {
      this.state.step += 1;
      if (this.state.step >= this.state.questions.length) {
        this.finishQuestions();
      } else {
        this.renderQuestion();
      }
    }, 180);
  }

  back() {
    if (this.state.step <= 0) return;
    this.state.step -= 1;
    this.renderQuestion();
  }

  finishQuestions() {
    const list = Object.values(this.paddles).map(p => ({
      paddle: p,
      score: scorePaddle(p, this.state.answers),
    })).sort((a, b) => b.score - a.score);

    this.state.scored = list;
    // Persist completed state so reload / nav away and back lands on results
    saveStoredState(this.state);
    phQuiz('quiz_completed', {
      context: this.dataset.context,
      top_match: list[0] && list[0].paddle.name,
      answers: this.state.answers,
    });
    if (SHOW_EMAIL_STEP) {
      this.showScreen('email');
    } else {
      // Email step disabled: skip straight to results (no Klaviyo subscribe/event yet).
      this.renderResults();
    }
  }

  handleEmailSubmit = (e) => {
    e.preventDefault();
    const input = this.$emailForm.querySelector('input[type="email"]');
    const email = (input?.value || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.$emailError.hidden = false;
      return;
    }
    this.$emailError.hidden = true;
    phQuiz('quiz_email_submitted', { context: this.dataset.context });
    this.submitToKlaviyo(email);
    this.renderResults();
  };

  skipEmail() {
    phQuiz('quiz_email_skipped', { context: this.dataset.context });
    this.fireKlaviyoEvent(null);
    this.renderResults();
  }

  async submitToKlaviyo(email) {
    if (!this.klaviyoListId || !this.klaviyoPublicKey) {
      console.warn('[paddle-quiz] Klaviyo not configured, skipping subscribe');
      this.fireKlaviyoEvent(email);
      return;
    }

    try {
      await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.klaviyoPublicKey)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    properties: this.buildProfileProperties(),
                  },
                },
              },
            },
            relationships: {
              list: { data: { type: 'list', id: this.klaviyoListId } },
            },
          },
        }),
      });
    } catch (err) {
      console.error('[paddle-quiz] Klaviyo subscribe failed', err);
    }

    this.fireKlaviyoEvent(email);
  }

  async fireKlaviyoEvent(email) {
    if (!this.klaviyoPublicKey) return;

    const top = this.state.scored[0]?.paddle;
    const absoluteUrl = top?.url
      ? (top.url.startsWith('http') ? top.url : `${window.location.origin}${top.url}`)
      : '';
    // Shopify's image_url returns a protocol-relative URL (//cdn.shopify.com/...)
    // which doesn't render in email clients — force https.
    const absoluteImage = top?.image
      ? (top.image.startsWith('//') ? `https:${top.image}` : top.image)
      : '';
    const profile = email
      ? { data: { type: 'profile', attributes: { email } } }
      : { data: { type: 'profile', attributes: { anonymous_id: this.getAnonymousId() } } };

    try {
      await fetch(`https://a.klaviyo.com/client/events/?company_id=${encodeURIComponent(this.klaviyoPublicKey)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: {
                answers: this.state.answers,
                top_match: top?.name,
                top_match_url: absoluteUrl,
                top_match_description: top?.description,
                top_match_image: absoluteImage,
                ranked_results: this.state.scored.map(r => ({ name: r.paddle.name, score: r.score })),
                context: this.dataset.context,
              },
              metric: { data: { type: 'metric', attributes: { name: this.klaviyoEventName } } },
              profile,
            },
          },
        }),
      });
    } catch (err) {
      console.error('[paddle-quiz] Klaviyo event failed', err);
    }
  }

  buildProfileProperties() {
    const top = this.state.scored[0]?.paddle;
    return {
      paddle_quiz_top_match: top?.name || '',
      paddle_quiz_answers: JSON.stringify(this.state.answers),
    };
  }

  getAnonymousId() {
    const KEY = 'friday_paddle_quiz_anon_id';
    let id = null;
    try { id = localStorage.getItem(KEY); } catch (e) {}
    if (!id) {
      id = 'pq_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
      try { localStorage.setItem(KEY, id); } catch (e) {}
    }
    return id;
  }

  renderResults() {
    const [top, ...rest] = this.state.scored;
    if (!top) return;

    const priceHtml = (key) => this.priceTemplates[key] || '';

    const statBar = (label, value) => `
      <div class="paddle-quiz__stat">
        <span class="paddle-quiz__stat-label">${label}</span>
        <span class="paddle-quiz__stat-track">
          <span class="paddle-quiz__stat-fill" style="width:${value * 10}%"></span>
        </span>
        <span class="paddle-quiz__stat-value">${value}</span>
      </div>
    `;

    const imageTag = (p, sizeClass = '') => p.image
      ? `<img class="paddle-quiz__result-image ${sizeClass}" src="${p.image}" alt="${p.name}" loading="lazy" />`
      : '';

    const topCard = `
      <article class="paddle-quiz__result paddle-quiz__result--top">
        <div class="paddle-quiz__result-badge">Best match</div>
        <div class="paddle-quiz__result-top">
          <a href="${top.paddle.url}" class="paddle-quiz__result-image-link">
            ${imageTag(top.paddle)}
          </a>
          <div class="paddle-quiz__result-meta">
            <div class="paddle-quiz__result-gen">${top.paddle.gen}</div>
            <a href="${top.paddle.url}" class="paddle-quiz__result-name-link">
              <h3 class="paddle-quiz__result-name heading">${top.paddle.name}</h3>
            </a>
            <p class="paddle-quiz__result-tagline">${top.paddle.tagline}</p>
            <div class="paddle-quiz__price-slot">${priceHtml(top.paddle.key)}</div>
          </div>
        </div>
        <p class="paddle-quiz__result-desc">${top.paddle.description}</p>
        <div class="paddle-quiz__stats">
          ${statBar('Power',       top.paddle.power)}
          ${statBar('Control',     top.paddle.control)}
          ${statBar('Spin',        top.paddle.spin)}
          ${statBar('Sweet spot',  top.paddle.sweetSpot)}
          ${statBar('Forgiveness', top.paddle.forgiveness)}
        </div>
        <a href="${top.paddle.url}" class="button button--primary paddle-quiz__shop-btn">
          <span class="btn-fill" data-fill></span>
          <span class="btn-text">Shop ${top.paddle.name}</span>
        </a>
      </article>
    `;

    const runnerUpHeader = rest.length
      ? `<h3 class="paddle-quiz__runner-header heading">Also worth considering</h3>`
      : '';

    const runnerCards = rest.map(r => `
      <article class="paddle-quiz__result paddle-quiz__result--runner">
        <div class="paddle-quiz__result-top">
          <a href="${r.paddle.url}" class="paddle-quiz__result-image-link">
            ${imageTag(r.paddle, 'paddle-quiz__result-image--sm')}
          </a>
          <div class="paddle-quiz__result-meta">
            <div class="paddle-quiz__result-gen">${r.paddle.gen}</div>
            <a href="${r.paddle.url}" class="paddle-quiz__result-name-link">
              <h4 class="paddle-quiz__result-name heading">${r.paddle.name}</h4>
            </a>
            <div class="paddle-quiz__price-slot">${priceHtml(r.paddle.key)}</div>
          </div>
        </div>
        <p class="paddle-quiz__result-why"><strong>Why pick this:</strong> ${r.paddle.whyRunnerUp}</p>
        <a href="${r.paddle.url}" class="paddle-quiz__result-link">View paddle →</a>
      </article>
    `).join('');

    this.$resultsBody.innerHTML = `
      ${topCard}
      ${runnerUpHeader}
      <div class="paddle-quiz__runners">
        ${runnerCards}
      </div>
    `;

    this.showScreen('results');
  }
}

if (!customElements.get('paddle-quiz')) {
  customElements.define('paddle-quiz', PaddleQuiz);
}

} // end double-load guard (window.__paddleQuizLoaded)
